import { MoreHorizontalIcon } from 'lucide-react'
import * as React from 'react'

import { Icon } from '#app/components/ui/icon.tsx'
import { LocalizedLink } from '#app/utils/i18n.tsx'
import { cn } from '#app/utils/misc.tsx'
import { type Button, buttonVariants } from './button'

function Pagination({ className, ...props }: React.ComponentProps<'nav'>) {
	return (
		<nav
			role="navigation"
			aria-label="pagination"
			data-slot="pagination"
			className={cn('mx-auto flex w-full justify-center', className)}
			{...props}
		/>
	)
}

function PaginationContent({
	className,
	...props
}: React.ComponentProps<'ul'>) {
	return (
		<ul
			data-slot="pagination-content"
			className={cn('flex flex-row items-center gap-1', className)}
			{...props}
		/>
	)
}

function PaginationItem({ ...props }: React.ComponentProps<'li'>) {
	return <li data-slot="pagination-item" {...props} />
}

type PaginationLinkProps = {
	isActive?: boolean
} & Pick<React.ComponentProps<typeof Button>, 'size'> &
	React.ComponentProps<typeof LocalizedLink>

function PaginationLink({
	className,
	isActive,
	size = 'icon',
	...props
}: PaginationLinkProps) {
	// If there's no link, I most likely want a button
	if (props.to === '#') {
		return (
			<button
				aria-current={isActive ? 'page' : undefined}
				data-slot="pagination-link"
				data-active={isActive}
				className={cn(
					buttonVariants({
						variant: isActive ? 'outline' : 'ghost',
						size,
					}),
					'aria-disabled:cursor-not-allowed aria-disabled:opacity-40',
					className,
				)}
				// @ts-expect-error
				onClick={
					props.onClick ??
					(() => {
						console.log('No onClick defined here!')
					})
				}
			>
				{props.children}
			</button>
		)
	}

	return (
		<LocalizedLink
			aria-current={isActive ? 'page' : undefined}
			data-slot="pagination-link"
			data-active={isActive}
			className={cn(
				buttonVariants({
					variant: isActive ? 'outline' : 'ghost',
					size,
				}),
				'aria-disabled:cursor-not-allowed aria-disabled:opacity-40',
				className,
			)}
			{...props}
			to={props['aria-disabled'] ? '#' : props.to}
		/>
	)
}

function PaginationPrevious({
	className,
	...props
}: React.ComponentProps<typeof PaginationLink>) {
	return (
		<PaginationLink
			aria-label="Go to previous page"
			size="default"
			className={cn('gap-1 px-2.5 sm:pl-2.5', className)}
			{...props}
		>
			<Icon name={'arrow-left'} size={'md'} />
		</PaginationLink>
	)
}

function PaginationNext({
	className,
	...props
}: React.ComponentProps<typeof PaginationLink>) {
	return (
		<PaginationLink
			aria-label="Go to next page"
			size="default"
			className={cn('gap-1 px-2.5 sm:pr-2.5', className)}
			{...props}
		>
			<Icon name={'arrow-right'} size={'md'} />
		</PaginationLink>
	)
}

function PaginationEllipsis({
	className,
	...props
}: React.ComponentProps<'span'>) {
	return (
		<span
			aria-hidden
			data-slot="pagination-ellipsis"
			className={cn('flex size-9 items-center justify-center', className)}
			{...props}
		>
			<MoreHorizontalIcon className="size-4" />
			<span className="sr-only">More pages</span>
		</span>
	)
}

export {
	Pagination,
	PaginationContent,
	PaginationLink,
	PaginationItem,
	PaginationPrevious,
	PaginationNext,
	PaginationEllipsis,
}
